"use strict";

import initializeSimpleModal from "../components/modals/simple";
import { isValidDocumentNumber } from "../customerDocument/validator";
import { CUSTOMER_DOCUMENT } from "../constants";
import {
    requestZipCodeAddress,
    resolveZipCodeRequestConfig
} from "../helpers/zipCodeRecaptchaRequest";
import {
    applyLeadCsrfToken,
    fetchLeadCsrfToken,
    resolveLeadCsrfTokenUrl
} from "../helpers/leadCsrf";

const allowedCustomFieldTypes = ["email", "number", "select", "tel", "text", "textarea"];

const escapeHtml = (value) => String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const normalizeCustomFieldId = (value) => String(value || "")
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[^A-Za-z0-9_-]/g, "")
    .substring(0, 40);

const normalizeCustomFieldType = (type) => {
    let normalizedType = String(type || "text").toLowerCase();

    if (normalizedType === "dropdown") {
        normalizedType = "select";
    }

    return allowedCustomFieldTypes.indexOf(normalizedType) >= 0 ? normalizedType : "text";
};

const parseRequiredFlag = (value) => {
    const normalizedValue = String(value || "").toLowerCase().trim();

    return normalizedValue === "true" ||
        normalizedValue === "sim" ||
        normalizedValue === "s" ||
        normalizedValue === "obrigatorio" ||
        normalizedValue === "required";
};

const normalizeCustomField = (field) => {
    if (!field || typeof field !== "object") {
        return null;
    }

    const label = field.label || field.name || field.title;
    const id    = normalizeCustomFieldId(field.id || field.key || field.name || label);

    if (!id || !label) {
        return null;
    }

    let type      = normalizeCustomFieldType(field.type);
    const options = [];

    if (type === "select" && field.options && field.options.length) {
        field.options.forEach((option) => {
            const optionLabel = typeof option === "string" ? option : option.label || option.name || option.value;
            const optionValue = typeof option === "string" ? option : option.value || optionLabel;

            if (optionLabel && optionValue) {
                options.push({
                    label: String(optionLabel),
                    value: String(optionValue)
                });
            }
        });
    }

    if (type === "select" && !options.length) {
        type = "text";
    }

    return {
        id,
        inputId: `customLeadField_${id}`,
        name: `customLeadField_${id}`,
        label: String(label),
        required: field.required === true || field.mandatory === true,
        type,
        options
    };
};

const parseCustomFieldsLines = (rawConfig) => {
    const fields = [];

    String(rawConfig).split(/\r?\n/).forEach((line) => {
        const trimmedLine = String(line || "").trim();

        if (!trimmedLine) {
            return;
        }

        const parts           = trimmedLine.split("|").map((part) => String(part || "").trim());
        const type            = normalizeCustomFieldType(parts[1] || "text");
        const options         = type === "select" && parts[3]
            ? parts[3].split(",").map((option) => option.trim()).filter(Boolean)
            : [];
        const normalizedField = normalizeCustomField({
            label: parts[0],
            type,
            required: parseRequiredFlag(parts[2]),
            options
        });

        if (normalizedField) {
            fields.push(normalizedField);
        }
    });

    return fields;
};

const parseCustomFieldsConfiguration = (rawConfig) => {
    if (!rawConfig || !String(rawConfig).trim()) {
        return [];
    }

    try {
        const parsedConfig = JSON.parse(String(rawConfig));
        const rawFields    = Array.isArray(parsedConfig) ? parsedConfig : parsedConfig.fields;

        return (rawFields || []).map(normalizeCustomField).filter(Boolean);
    } catch (error) {
        return parseCustomFieldsLines(rawConfig);
    }
};

const getCustomFieldMarkup = (field, formId) => {
    const inputId       = `${formId}_${field.inputId}`;
    const requiredAttrs = field.required ? "data-required=\"true\" required" : "data-required=\"false\"";
    let fieldMarkup;

    if (field.type === "textarea") {
        fieldMarkup = `<textarea class="form-control w-100" id="${escapeHtml(inputId)}" name="${escapeHtml(field.name)}" placeholder=" " data-missing-error=" " ${requiredAttrs}></textarea>`;
    } else if (field.type === "select") {
        const options = field.options.map((option) => `<option value="${escapeHtml(option.value)}">${escapeHtml(option.label)}</option>`).join("");

        fieldMarkup = `<select class="form-control w-100" id="${escapeHtml(inputId)}" name="${escapeHtml(field.name)}" ${requiredAttrs}><option value="" selected></option>${options}</select>`;
    } else {
        fieldMarkup = `<input class="form-control w-100" type="${escapeHtml(field.type)}" id="${escapeHtml(inputId)}" name="${escapeHtml(field.name)}" placeholder=" " data-missing-error=" " ${requiredAttrs} />`;
    }

    return `<div class="form-group w-100"><div class="text-field w-100">${fieldMarkup}<label for="${escapeHtml(inputId)}">${escapeHtml(field.label)}</label></div><div class="invalid-feedback"></div></div>`;
};

const renderCustomLeadFields = ($leadForm) => {
    const rawConfig = $(".js-custom-lead-fields-config", $leadForm).val();
    const fields    = parseCustomFieldsConfiguration(rawConfig);
    const formId    = $leadForm.attr("id") || "leadForm";
    const markup    = fields.map((field) => getCustomFieldMarkup(field, formId)).join("");

    $(".js-custom-lead-fields", $leadForm).html(markup);
    $(".js-custom-lead-fields-definition", $leadForm).val(JSON.stringify(fields.map((field) => ({
        id: field.id,
        name: field.name,
        label: field.label,
        type: field.type
    }))));
};

const sanitizeDocumentNumber = (documentNumber) => String(documentNumber || "")
    .replace(/[./\s-]/g, "")
    .toUpperCase();

const hasCompleteName = (fullName) => String(fullName || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .length >= 2;

const getLeadDocumentType = (documentNumber) => {
    const sanitizedDocumentNumber = sanitizeDocumentNumber(documentNumber);

    return sanitizedDocumentNumber.length > 11 ? CUSTOMER_DOCUMENT.CNPJ : CUSTOMER_DOCUMENT.CPF;
};

const getLeadFormData = ($leadForm, isBasicLead, originSystem) => {
    const fieldsMap  = {
        fullName: "#name",
        email: "#email",
        documentNumber: "#cpfcnpj",
        cellphone: "#phone",
        companyName: "#companyName",
        jobTitle: "#jobTitle",
        employeeCount: "#employeeCount",
        segment: "#segment",
        zipcode: "#zipcode",
        locality: "#locality",
        solutionInterest: "#solutionInterest",
        regionState: "#regionState",
        lgpdOptIn: "#lgpdOptIn",
        campaignId: "#campaignId",
        subcategory: "#subcategory",
        person: "#person",
        position: "#position",
        leadType: "#leadType",
        dataCloudCategory: "#dataCloudCategory",
        leadDestination: "[name='leadDestination']",
        isAlgarCustomer: "[name='isAlgarCustomer']"
    };
    const fieldsData = $leadForm.serializeArray();

    Object.keys(fieldsMap).forEach((fieldName) => {
        const $field = $(fieldsMap[fieldName], $leadForm);

        if (!$field.length) {
            return;
        }

        fieldsData.push({
            name: fieldName,
            value: $field.is(":checkbox") ? String($field.is(":checked")) : ($field.val() || "")
        });
    });

    fieldsData.push({ name: "isBasicLead", value: isBasicLead });
    fieldsData.push({ name: "originIntegrationType", value: originSystem || "" });

    return $.param(fieldsData);
};

const clearLeadAddressByZipCode = ($leadForm) => {
    $("#locality", $leadForm).val("").trigger("change");
    $("#regionState", $leadForm).val("").trigger("change");
};

const populateLeadAddressByZipCode = ($leadForm, response) => {
    if (!response || !response.success || !response.addressList || !response.addressList.length) {
        return false;
    }

    const address = response.addressList[0];

    $("#locality", $leadForm).val(address.city || "").trigger("change");
    $("#regionState", $leadForm).val(address.stateCode || address.state || "").trigger("change");

    return true;
};

const setupLeadZipCodeAutoFill = ($leadForm) => {
    const $zipCodeField = $("#zipcode", $leadForm);

    if (!$zipCodeField.length) {
        return;
    }

    const urlFromDom            = $zipCodeField.attr("data-get-address-by-cep-url")
        || $leadForm.attr("data-get-address-by-cep-url");
    const resolvedZipCodeConfig = resolveZipCodeRequestConfig(
        urlFromDom,
        $zipCodeField.attr("data-recaptcha-required")
        || $leadForm.attr("data-recaptcha-required")
    );
    const url                   = resolvedZipCodeConfig.url;
    const isRecaptchaRequired   = resolvedZipCodeConfig.isRecaptchaRequired;
    const zipCodeNotFoundMsg    = $leadForm.attr("data-zip-code-not-found-msg") || "Nenhum endereço foi encontrado para o CEP informado, tente novamente.";
    const recaptchaFailedMsg    = $leadForm.attr("data-recaptcha-failed-msg") || zipCodeNotFoundMsg;
    const zipCodeMaxLength      = 9;
    const $zipCodeInputParent   = $zipCodeField.closest(".text-field");
    const $zipCodeInvalidFeedback = $zipCodeField.closest(".form-group").find(".invalid-feedback");
    let zipCodeLookupRequestId = 0;

    if (!url) {
        return;
    }

    const setZipCodeLoadingState = (isLoading) => {
        $zipCodeInputParent.toggleClass("input-loading", isLoading);
    };

    const setZipCodeValidationState = (isValid, message) => {
        $zipCodeField.toggleClass("is-invalid", !isValid);
        $zipCodeInvalidFeedback.text(isValid ? "" : (message || zipCodeNotFoundMsg));
    };

    const handleZipCodeLookupResponse = (zipCodeValue, response, requestId, isRecaptchaRetry) => {
        if (requestId !== zipCodeLookupRequestId) {
            return;
        }

        setZipCodeLoadingState(false);

        if (response.recaptchaFailed) {
            const isBrowserError = Array.isArray(response.recaptchaErrorCodes)
                && response.recaptchaErrorCodes.indexOf("browser-error") !== -1;

            if (isBrowserError && !isRecaptchaRetry) {
                requestAddressByZipCode(zipCodeValue, true);
                return;
            }

            setZipCodeValidationState(false, recaptchaFailedMsg);
            clearLeadAddressByZipCode($leadForm);
            return;
        }

        if (populateLeadAddressByZipCode($leadForm, response)) {
            setZipCodeValidationState(true);
            return;
        }

        setZipCodeValidationState(false, zipCodeNotFoundMsg);
        clearLeadAddressByZipCode($leadForm);
    };

    const requestAddressByZipCode = (zipCodeValue, isRecaptchaRetry) => {
        const requestId = ++zipCodeLookupRequestId;

        setZipCodeLoadingState(true);

        requestZipCodeAddress({
            url: url,
            zipCodeValue: zipCodeValue,
            isRecaptchaRequired: isRecaptchaRequired,
            onSuccess: function (response) {
                handleZipCodeLookupResponse(zipCodeValue, response, requestId, isRecaptchaRetry);
            },
            onError: function () {
                if (requestId !== zipCodeLookupRequestId) {
                    return;
                }

                setZipCodeLoadingState(false);
                setZipCodeValidationState(false, zipCodeNotFoundMsg);
                clearLeadAddressByZipCode($leadForm);
            },
            onRecaptchaUnavailable: function () {
                if (requestId !== zipCodeLookupRequestId) {
                    return;
                }

                setZipCodeLoadingState(false);
                setZipCodeValidationState(false, recaptchaFailedMsg);
                clearLeadAddressByZipCode($leadForm);
            }
        });
    };

    $zipCodeField.off("keyup.leadFormZipCode change.leadFormZipCode")
        .on("keyup.leadFormZipCode change.leadFormZipCode", function () {
            const zipCodeValue     = String($zipCodeField.val() || "");
            const zipCodePrevValue = $zipCodeField.data("previous-value");

            if (zipCodeValue === zipCodePrevValue) {
                return;
            }

            $zipCodeField.data("previous-value", zipCodeValue);
            setZipCodeValidationState(true);

            if (zipCodeValue.length < zipCodeMaxLength) {
                zipCodeLookupRequestId += 1;
                clearLeadAddressByZipCode($leadForm);
                return;
            }

            if (zipCodeValue.length === zipCodeMaxLength) {
                requestAddressByZipCode(zipCodeValue);
            }
        });
};

const initializeLeadForms = () => {
    const leadForms       = $("form[name='lead-form']");
    const modalSiteAnchor = $("#modal-anchor");

    leadForms.each((index, leadForm) => {
        const $leadForm = $(leadForm);

        if ($leadForm.data("lead-form-initialized")) {
            return;
        }

        $leadForm.data("lead-form-initialized", true);
        renderCustomLeadFields($leadForm);
        setupLeadZipCodeAutoFill($leadForm);

        const $allInputFields          = $("input, select, textarea", leadForm);
        const $selectFields            = $("select", leadForm);
        const $submitButton            = $("button", leadForm);
        const $documentField           = $("#cpfcnpj", leadForm);
        const $isAlgarCustomerField    = $(".js-is-algar-customer", leadForm);
        let algarCustomerCheckTimer    = null;
        let algarCustomerCheckRequest  = null;
        let algarCustomerCheckDocument = null;

        const isRequiredField       = ($field) => $field.data("required") === true || $field.data("required") === "true" || $field.prop("required");
        const hasValidRequiredValue = ($field, minLength) => {
            if (!$field.length) {
                return true;
            }

            if ($field.attr("type") === "checkbox") {
                return !isRequiredField($field) || $field.prop("checked");
            }

            const value = $field.val() || "";

            if (!isRequiredField($field) && !value.length) {
                return true;
            }

            return minLength ? value.length >= minLength : value.length > 0;
        };

        const getMissingFieldMessage = ($field) => {
            const fieldLabel = $field.closest(".form-group").find("label").first().text().trim();

            return fieldLabel ? `${fieldLabel} é obrigatório.` : "Campo obrigatório.";
        };

        const clearInvalidFeedback = ($input) => {
            const $formGroup = $input.closest(".form-group");

            $input.removeClass("is-invalid");
            $formGroup.find(".invalid-feedback").text("");
        };

        const updateSubmitState = () => {
            $submitButton.prop("disabled", false);
            $submitButton.removeAttr("disabled");
            $leadForm.data("disabled", false);
        };

        const updateSelectState = (selectField) => {
            const $selectField  = $(selectField);
            const selectedValue = $selectField.val();

            $selectField.toggleClass("has-value", Boolean(selectedValue));
        };

        const setIsAlgarCustomer = (value, documentNumber) => {
            $isAlgarCustomerField.val(value ? "true" : "false");
            $isAlgarCustomerField.data("checked-document", documentNumber || "");
        };

        const checkAlgarCustomer = () => {
            const url               = $leadForm.data("check-algar-customer-url");
            const rawDocumentNumber = $documentField.val();
            const documentNumber    = sanitizeDocumentNumber(rawDocumentNumber);
            const documentType      = getLeadDocumentType(rawDocumentNumber);

            if (!url || !$documentField.length || !documentNumber || !isValidDocumentNumber(rawDocumentNumber, documentType)) {
                setIsAlgarCustomer(false, "");
                return;
            }

            if ($isAlgarCustomerField.data("checked-document") === documentNumber) {
                return;
            }

            if (algarCustomerCheckDocument === documentNumber) {
                return;
            }

            if (algarCustomerCheckRequest) {
                algarCustomerCheckRequest.abort();
            }

            algarCustomerCheckDocument = documentNumber;
            algarCustomerCheckRequest  = $.ajax({
                url,
                type: "GET",
                data: {
                    customerDocumentNumber: documentNumber
                },
                success: function (response) {
                    const currentDocumentNumber = sanitizeDocumentNumber($documentField.val());

                    if (currentDocumentNumber !== documentNumber) {
                        return;
                    }

                    setIsAlgarCustomer(Boolean(response && response.success && response.isAlgarCustomer), documentNumber);
                },
                error: function () {
                    setIsAlgarCustomer(false, documentNumber);
                },
                complete: function () {
                    algarCustomerCheckRequest  = null;
                    algarCustomerCheckDocument = null;
                }
            });
        };

        const scheduleAlgarCustomerCheck = () => {
            clearTimeout(algarCustomerCheckTimer);
            algarCustomerCheckTimer = setTimeout(checkAlgarCustomer, 500);
        };

        $selectFields.each((selectIndex, selectField) => updateSelectState(selectField));
        $selectFields.on("change blur", (event) => updateSelectState(event.currentTarget));
        $selectFields.on("focus", (event) => $(event.currentTarget).addClass("has-value"));
        $documentField.off("input.leadFormAlgarCustomer blur.leadFormAlgarCustomer");
        $documentField.on("input.leadFormAlgarCustomer blur.leadFormAlgarCustomer", scheduleAlgarCustomerCheck);

        $allInputFields.on("keyup change", updateSubmitState);
        $allInputFields.on("keyup change", (event) => {
            const $field    = $(event.currentTarget);
            const minLength = Number($field.attr("minlength")) || null;

            if (hasValidRequiredValue($field, minLength)) {
                clearInvalidFeedback($field);
            }
        });
        updateSubmitState();

        function formIsInvalid(leadForm) {
            const emailInput                  = leadForm.querySelector("#email");
            const emailValue                  = emailInput ? emailInput.value || "" : "";
            const shouldValidateEmailSafety   = emailValue.length || isRequiredField($(emailInput));
            const isEmailInvalidSafetyMail    = shouldValidateEmailSafety && emailInput && (emailInput.style.backgroundImage).includes("invalid-safetyoptin.png");
            const isEmailValidatingSafetyMail = shouldValidateEmailSafety && emailInput && (emailInput.style.backgroundImage).includes("spinner.gif");

            return $leadForm.data("disabled") || isEmailInvalidSafetyMail || isEmailValidatingSafetyMail;
        }

        const populateInvalidFeedback = ($input, message) => {
            const $formGroup       = $input.closest(".form-group");
            const $invalidFeedback = $formGroup.find(".invalid-feedback");

            $input.addClass("is-invalid");
            $input.removeClass("success");
            $invalidFeedback.text(message);
        };

        const populateRequiredFieldsFeedback = () => {
            let isValid = true;

            $("[data-required='true']", leadForm).each((fieldIndex, field) => {
                const $field    = $(field);
                const minLength = Number($field.attr("minlength")) || null;

                if (!hasValidRequiredValue($field, minLength)) {
                    populateInvalidFeedback($field, getMissingFieldMessage($field));
                    isValid = false;
                }
            });

            return isValid;
        };

        $(leadForm).submit((event) => {
            event.preventDefault();

            const emailInput   = leadForm.querySelector("#email");
            const cpfcnpjInput = leadForm.querySelector("#cpfcnpj");

            const isInvalidForm = formIsInvalid(leadForm);

            const $fullNameField = $(".js-full-name", leadForm);

            if ($fullNameField.length && !hasCompleteName($fullNameField.val())) {
                populateInvalidFeedback($fullNameField, "É obrigatório informar o nome completo.");
                return;
            }

            if (!populateRequiredFieldsFeedback()) {
                return;
            }

            if (isInvalidForm) {
                populateInvalidFeedback($(emailInput), "Email inválido");
                return;
            }

            const cnpjOnlyRaw       = $leadForm.data("requires-cnpj-only");
            const cnpjOnly          = cnpjOnlyRaw === true || String(cnpjOnlyRaw) === "true";
            const isCnpjMaskedField =
                cpfcnpjInput
                && (cpfcnpjInput.classList.contains("js-cleave-cnpj") || cnpjOnly);

            if (cpfcnpjInput && cpfcnpjInput.value) {
                const documentType = isCnpjMaskedField
                    ? CUSTOMER_DOCUMENT.CNPJ
                    : getLeadDocumentType(cpfcnpjInput.value);

                if (!isValidDocumentNumber(cpfcnpjInput.value, documentType)) {
                    const invalidDocumentMessage = documentType === CUSTOMER_DOCUMENT.CNPJ
                        ? "CNPJ inválido."
                        : "CPF inválido.";

                    populateInvalidFeedback($(cpfcnpjInput), invalidDocumentMessage);
                    return;
                }
            }

            $leadForm.find(".js-current-page-url").val(window.location.href);
            $leadForm.find(".js-before-page-url").val(document.referrer);

            $submitButton.removeClass("btn-primary").addClass("btn-primary-loading");

            const isBasicLead     = $leadForm.data("is-basic-lead");
            const generateLeadUrl = $leadForm.data("generate-lead-url");
            const modalUrl        = $(leadForm).attr("action");
            const originSystem    = $("#originSystem", leadForm).val();
            const submitErrorMsg  = $leadForm.attr("data-submit-error-msg")
                || "Não foi possível enviar o formulário. Verifique os dados e tente novamente.";

            const restoreSubmitButton = () => {
                $submitButton.addClass("btn-primary").removeClass("btn-primary-loading");
            };

            const showSubmitError = () => {
                restoreSubmitButton();

                const $errorField = $(cpfcnpjInput || emailInput);

                if ($errorField.length) {
                    populateInvalidFeedback($errorField, submitErrorMsg);
                }
            };

            const csrfTokenUrl = resolveLeadCsrfTokenUrl(
                generateLeadUrl,
                $leadForm.attr("data-csrf-token-url")
            );

            fetchLeadCsrfToken(csrfTokenUrl).then(function (csrf) {
                $.ajax({
                    url: generateLeadUrl,
                    data: applyLeadCsrfToken(
                        $leadForm,
                        getLeadFormData($leadForm, isBasicLead, originSystem),
                        csrf
                    ),
                    type: "POST",
                    dataType: "json"
                }).done(function (response) {
                    if (!response || response.error || response.success === false || response.csrfError) {
                        showSubmitError();
                        return;
                    }

                    $.ajax({
                        url: modalUrl,
                        type: "get",
                        dataType: "html",
                        success: function (data) {
                            setTimeout(restoreSubmitButton, 1000);
                            $(modalSiteAnchor).append(data);
                            initializeSimpleModal();
                            $(leadForm)[0].reset();
                            $selectFields.each((selectIndex, selectField) => updateSelectState(selectField));
                        },
                        error: function () {
                            setTimeout(restoreSubmitButton, 1000);
                        }
                    });
                }).fail(showSubmitError);
            });
        });
    });
};

export default initializeLeadForms;
